import React, { useEffect, useMemo } from "react";
import { QueryClient, QueryClientProvider } from "react-query";

export const MOCK_ORDERS = [
  {
    id: "WWG897645",
    date: "2026-03-12T10:00:00.000Z",
    tracking: "1Z999AA10123456784",
    isPreOrder: true,
    subtotal: 189.98,
    tax: 15.2,
    total: 205.18,
    billingAddress: {
      street: "1313 Mockingbird Lane",
      city: "Mockingbird Heights",
      state: "CA",
      zip: "90210",
    },
    paymentMethod: {
      type: "Visa",
      last4: "4242",
    },
    items: [
      {
        stockId: "book-101",
        title: "The Leadership Blueprint",
        imageUrl: "https://picsum.photos/seed/book-101/220/220",
        memberPrice: 89.99,
        author: "A. Carter",
      },
      {
        stockId: "book-102",
        title: "Winning Habits Journal",
        imageUrl: "https://picsum.photos/seed/book-102/220/220",
        memberPrice: 99.99,
      },
    ],
  },
  {
    id: "WWG897646",
    date: "2026-02-19T12:10:00.000Z",
    tracking: "1Z999AA10123456785",
    isPreOrder: false,
    subtotal: 54.99,
    tax: 4.4,
    total: 59.39,
    billingAddress: {
      street: "42 Harbor Road",
      city: "Seattle",
      state: "WA",
      zip: "98101",
    },
    paymentMethod: {
      type: "Visa",
      last4: "1111",
    },
    items: [
      {
        stockId: "book-103",
        title: "Daily Growth Planner",
        imageUrl: "https://picsum.photos/seed/book-103/220/220",
        memberPrice: 54.99,
      },
    ],
  },
  {
    id: "WWG897647",
    date: "2025-11-03T09:35:00.000Z",
    tracking: "1Z999AA10123456786",
    isPreOrder: false,
    subtotal: 129.97,
    tax: 10.4,
    total: 140.37,
    billingAddress: {
      street: "995 Market Street",
      city: "San Francisco",
      state: "CA",
      zip: "94103",
    },
    paymentMethod: {
      type: "Visa",
      last4: "2222",
    },
    items: [
      {
        stockId: "book-104",
        title: "Sales Mastery Playbook",
        imageUrl: "https://picsum.photos/seed/book-104/220/220",
        memberPrice: 39.99,
        color: "Black",
        size: "M",
      },
      {
        stockId: "book-105",
        title: "Closing Conversations",
        imageUrl: "https://picsum.photos/seed/book-105/220/220",
        memberPrice: 44.99,
        color: "Blue",
        size: "L",
      },
      {
        stockId: "book-106",
        title: "Influence in Action",
        imageUrl: "https://picsum.photos/seed/book-106/220/220",
        memberPrice: 44.99,
        author: "N. Walker",
      },
    ],
  },
  {
    id: "WWG897648",
    date: "2025-08-16T16:20:00.000Z",
    tracking: "1Z999AA10123456787",
    isPreOrder: false,
    subtotal: 24.99,
    tax: 2,
    total: 26.99,
    billingAddress: {
      street: "9 Cherry Blossom Ave",
      city: "Austin",
      state: "TX",
      zip: "73301",
    },
    paymentMethod: {
      type: "Visa",
      last4: "3333",
    },
    items: [
      {
        stockId: "book-107",
        title: "Momentum Notes",
        imageUrl: "https://picsum.photos/seed/book-107/220/220",
        memberPrice: 24.99,
      },
    ],
  },
];

const MOCK_CART = {
  items: [{ id: "cart-item-1" }, { id: "cart-item-2" }],
};

const okResponse = (body) =>
  Promise.resolve({
    ok: true,
    status: 200,
    json: async () => body,
  });

const errorResponse = (status) =>
  Promise.resolve({
    ok: false,
    status,
    json: async () => ({ message: `Mock error ${status}` }),
  });

const toPath = (input) => {
  if (typeof Request !== "undefined" && input instanceof Request) {
    return new URL(input.url, window.location.origin).pathname;
  }

  const raw = input instanceof URL ? input.toString() : String(input);
  return new URL(raw, window.location.origin).pathname;
};

const normalizePath = (path) => {
  const ordersIndex = path.indexOf("/orders");
  if (ordersIndex >= 0) {
    return path.slice(ordersIndex);
  }

  const cartIndex = path.indexOf("/cart");
  if (cartIndex >= 0) {
    return path.slice(cartIndex);
  }

  return path;
};

const getOrderIdFromPath = (path) => {
  const parts = path.split("/").filter(Boolean);
  const ordersIndex = parts.indexOf("orders");

  if (ordersIndex < 0 || !parts[ordersIndex + 1]) {
    return null;
  }

  return decodeURIComponent(parts[ordersIndex + 1]);
};

export const installOrdersStorybookMocks = () => {
  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input, init = {}) => {
    const method = (init.method || "GET").toUpperCase();
    const path = normalizePath(toPath(input));

    if (method === "GET" && path === "/cart") {
      return okResponse(MOCK_CART);
    }

    if (method === "GET" && path === "/orders") {
      return okResponse(MOCK_ORDERS);
    }

    if (method === "GET" && path.startsWith("/orders/")) {
      const orderId = getOrderIdFromPath(path);
      const order = MOCK_ORDERS.find((item) => item.id === orderId);
      return order ? okResponse(order) : errorResponse(404);
    }

    if (
      method === "POST" &&
      path.startsWith("/orders/") &&
      path.endsWith("/buy-again")
    ) {
      return okResponse({ success: true });
    }

    if (
      method === "PUT" &&
      path.startsWith("/orders/") &&
      path.endsWith("/shipping")
    ) {
      return okResponse({ success: true });
    }

    if (
      method === "POST" &&
      path.startsWith("/orders/") &&
      path.endsWith("/support")
    ) {
      return okResponse({ success: true });
    }

    if (method === "DELETE" && path.startsWith("/orders/")) {
      return okResponse({ success: true });
    }

    return originalFetch(input, init);
  };

  return () => {
    window.fetch = originalFetch;
  };
};

export const OrdersStorybookProvider = ({ children }) => {
  const queryClient = useMemo(() => {
    const client = new QueryClient({
      defaultOptions: {
        queries: {
          retry: false,
          staleTime: Infinity,
        },
      },
    });

    // Seed cache to avoid initial loading flash in stories.
    client.setQueryData("cart", MOCK_CART);
    client.setQueryData(["orders", {}], MOCK_ORDERS);
    MOCK_ORDERS.forEach((order) => {
      client.setQueryData(["order", order.id], order);
    });

    return client;
  }, []);

  useEffect(() => {
    const cleanup = installOrdersStorybookMocks();
    return () => cleanup();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  );
};
